import { inject, Injectable, Signal, signal } from '@angular/core';
import { BaseService } from '@fluxonos/features/shared/services/base-service';
import { HttpClient } from '@angular/common/http';
import { environment } from '@fluxonos/environments/environment';
import { map, Observable, tap } from 'rxjs';
import {
  AddUserRequestDto,
  FluxonosUtils,
  SearchRequestDto,
  SearchResponseDto,
  UpdateUserRequestDto,
  UserDto,
  UserPublicDto
} from '@fluxonos/fluxonos-dto';

@Injectable({
  providedIn: 'root'
})
export class UserService extends BaseService {
  private readonly _httpClient: HttpClient = inject(HttpClient);

  private readonly _apiBase = environment.features.userModule.user;

  private readonly _user = signal<UserDto | null>(null);

  public get(): Observable<UserDto> {
    const endpoint = this.getEndpoint(this._apiBase.get);

    return this._httpClient
      .request<UserDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        }
      })
      .pipe(
        map((res) => FluxonosUtils.transformToDto(UserDto, res)),
        tap((res) => {
          this._user.set(res);
        })
      );
  }

  public getById(id: string): Observable<UserDto> {
    const endpoint = this.getEndpoint(this._apiBase.getById, id);

    return this._httpClient
      .request<UserDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        }
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(UserDto, res)));
  }

  public search(search?: SearchRequestDto): Observable<SearchResponseDto<UserPublicDto>> {
    const endpoint = this.getEndpoint(this._apiBase.search);

    const params = this.buildHttpParams(search);

    return this._httpClient
      .request<SearchResponseDto<UserPublicDto>>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        params: params
      })
      .pipe(
        map((res) => {
          res.data = FluxonosUtils.transformToDtoList(UserPublicDto, res.data);
          return FluxonosUtils.transformToDto(SearchResponseDto, res);
        })
      );
  }

  public addUser(data: AddUserRequestDto): Observable<UserDto> {
    const endpoint = this.getEndpoint(this._apiBase.addUser);

    return this._httpClient
      .request<UserDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        body: data
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(UserDto, res)));
  }

  public patchUser(data: UpdateUserRequestDto): Observable<UserDto> {
    const endpoint = this.getEndpoint(this._apiBase.patchUser);

    return this._httpClient
      .request<UserDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        body: data
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(UserDto, res)));
  }

  public patchByUserId(id: string, data: UpdateUserRequestDto): Observable<UserDto> {
    const endpoint = this.getEndpoint(this._apiBase.patchByUserId, id);

    return this._httpClient
      .request<UserDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        body: data
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(UserDto, res)));
  }

  public deleteById(id: string): Observable<boolean> {
    const endpoint = this.getEndpoint(this._apiBase.deleteById, id);

    return this._httpClient.request<boolean>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header['contentType']
      }
    });
  }

  public cleanUp(): void {
    this._user.set(null);
  }

  public get user(): Signal<UserDto | null> {
    return this._user.asReadonly();
  }
}
